import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from '@/components/ui/responsive-modal';
import {
  availabilityOverridesApi,
  type AvailabilityOverride,
} from '@/services/api';
import TimeInput24h from '@/components/page-components/project/agents/ui/TimeInput24h';
import {
  normalizeTimeToHHmm,
  formatTimeForSupabase,
  normalizeOverrideDate,
  isAvailabilityOverridePast,
} from '@/utils/availability';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useToast } from '@/hooks/useToast';
import {
  Plus,
  Pencil,
  Trash2,
  Calendar as CalendarIcon,
  ChevronDown,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { CardTitle } from '@/components/ui/card';
import { useDateLocale } from '@/hooks/useDateLocale';
import { useDateFormat } from '@/hooks/useDateFormat';

interface AvailabilityOverridesProps {
  agentId: number;
  /** When provided with onOverridesChange, skips mount fetch and uses parent cache */
  overrides?: AvailabilityOverride[];
  isLoading?: boolean;
  onOverridesChange?: (overrides: AvailabilityOverride[]) => void;
}

export const AvailabilityOverrides: React.FC<AvailabilityOverridesProps> = ({
  agentId,
  overrides: overridesProp,
  isLoading: isLoadingProp,
  onOverridesChange,
}) => {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const df = useDateFormat();
  const { toast } = useToast();
  const isManagedExternally = onOverridesChange != null;
  const [overrides, setOverrides] = useState<AvailabilityOverride[]>(
    overridesProp ?? []
  );
  const [isLoading, setIsLoading] = useState(
    isManagedExternally ? Boolean(isLoadingProp) : false
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formDate, setFormDate] = useState(() =>
    new Date().toISOString().slice(0, 10)
  );
  const [formStartTime, setFormStartTime] = useState('09:00');
  const [formEndTime, setFormEndTime] = useState('17:00');
  const [formIsClosed, setFormIsClosed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [isPastOpen, setIsPastOpen] = useState(false);
  const selectedFormDate = formDate ? parseISO(formDate) : undefined;

  const { upcomingOverrides, pastOverrides } = useMemo(() => {
    const now = new Date();
    const upcoming: AvailabilityOverride[] = [];
    const past: AvailabilityOverride[] = [];
    for (const o of overrides) {
      if (isAvailabilityOverridePast(o, now)) past.push(o);
      else upcoming.push(o);
    }
    return { upcomingOverrides: upcoming, pastOverrides: past };
  }, [overrides]);

  useEffect(() => {
    if (!isManagedExternally || overridesProp === undefined) return;
    setOverrides(overridesProp);
  }, [isManagedExternally, overridesProp]);

  useEffect(() => {
    if (!isManagedExternally || isLoadingProp === undefined) return;
    setIsLoading(isLoadingProp);
  }, [isManagedExternally, isLoadingProp]);

  const fetchOverrides = async () => {
    if (!agentId) return;
    if (!isManagedExternally) setIsLoading(true);
    try {
      const data = await availabilityOverridesApi.fetchByAgent(agentId);
      if (isManagedExternally) {
        onOverridesChange?.(data);
      } else {
        setOverrides(data);
      }
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.availability_overrides.errors.fetch.translation',
          'Failed to load overrides'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      if (!isManagedExternally) setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isManagedExternally) return;
    if (agentId) void fetchOverrides();
  }, [agentId, isManagedExternally]);

  const openCreate = () => {
    setEditingId(null);
    setFormDate(new Date().toISOString().slice(0, 10));
    setFormStartTime('09:00');
    setFormEndTime('17:00');
    setFormIsClosed(false);
    setIsDialogOpen(true);
  };

  const openEdit = (o: AvailabilityOverride) => {
    if (o.id == null) return;
    setEditingId(o.id);
    const dateStr =
      normalizeOverrideDate(o.date) || new Date().toISOString().slice(0, 10);
    setFormDate(dateStr);
    setFormStartTime(normalizeTimeToHHmm(o.start_time));
    setFormEndTime(normalizeTimeToHHmm(o.end_time));
    setFormIsClosed(o.is_closed);
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formDate) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.availability_overrides.errors.invalid_date.translation',
          'Date required'
        ),
      });
      return;
    }
    const isValidTime = (t: string) =>
      /^(0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/.test(t);
    if (
      !formIsClosed &&
      (formStartTime >= formEndTime ||
        !isValidTime(formStartTime) ||
        !isValidTime(formEndTime))
    ) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.availability_overrides.errors.invalid_range.translation',
          'End time must be after start time'
        ),
      });
      return;
    }
    setIsSaving(true);
    try {
      if (editingId != null) {
        await availabilityOverridesApi.update(editingId, {
          date: formDate,
          start_time: formatTimeForSupabase(formStartTime),
          end_time: formatTimeForSupabase(formEndTime),
          is_closed: formIsClosed,
        });
        toast({
          title: t(
            'agent.availability_overrides.updated.translation',
            'Override updated'
          ),
        });
      } else {
        await availabilityOverridesApi.create({
          agent_id: agentId,
          date: formDate,
          start_time: formatTimeForSupabase(formStartTime),
          end_time: formatTimeForSupabase(formEndTime),
          is_closed: formIsClosed,
        });
        toast({
          title: t(
            'agent.availability_overrides.created.translation',
            'Override added'
          ),
        });
      }
      setIsDialogOpen(false);
      await fetchOverrides();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.availability_overrides.errors.save_failed.translation',
          'Failed to save override'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await availabilityOverridesApi.delete(id);
      toast({
        title: t(
          'agent.availability_overrides.deleted.translation',
          'Override removed'
        ),
      });
      setDeleteId(null);
      await fetchOverrides();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t(
          'agent.availability_overrides.errors.delete_failed.translation',
          'Failed to delete override'
        ),
        description:
          error instanceof Error
            ? error.message
            : t('common.unknown_error.translation', 'Unknown error'),
      });
    }
  };

  const formatDisplayDate = (dateStr: string, compact = false) => {
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return compact ? df.dateWithWeekday(d) : df.dateLong(d);
  };

  const renderOverrideItem = (
    o: AvailabilityOverride,
    { isPast = false }: { isPast?: boolean } = {}
  ) => {
    const dateStr = normalizeOverrideDate(o.date);
    return (
      <li
        key={o.id}
        className="flex items-center justify-between gap-2 rounded-full bg-card p-2.5 sm:p-3"
      >
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-medium">
            {formatDisplayDate(dateStr, true)}
          </span>
          {o.is_closed ? (
            <span className="ml-1.5 text-muted-foreground">
              {t('agent.availability_overrides.closed.translation', 'Closed')}
            </span>
          ) : (
            <span className="ml-1.5 whitespace-nowrap text-muted-foreground">
              {normalizeTimeToHHmm(o.start_time)}–
              {normalizeTimeToHHmm(o.end_time)}
            </span>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-0.5">
          {!isPast && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => openEdit(o)}
              aria-label={t('common.edit.translation', 'Edit')}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setDeleteId(o.id ?? null)}
            aria-label={t('common.delete.translation', 'Delete')}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      </li>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <CardTitle
            description={t(
              'agent.availability_overrides.description.translation',
              'Set date-specific hours or mark days as closed (e.g. holidays, vacation). Overrides take precedence over business hours.'
            )}
          >
            {t(
              'agent.availability_overrides.title.translation',
              'Availability Overrides'
            )}
          </CardTitle>
        </div>
        <Button
          type="button"
          variant="brand"
          size="icon"
          onClick={openCreate}
          className="shrink-0 lg:h-9 lg:w-auto lg:gap-2 lg:px-4 lg:py-2"
          aria-label={t(
            'agent.availability_overrides.add.translation',
            'Add Override'
          )}
        >
          <Plus className="h-4 w-4" />
          <span className="hidden lg:inline">
            {t('agent.availability_overrides.add.translation', 'Add Override')}
          </span>
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">
          {t('common.loading.translation', 'Loading...')}
        </p>
      ) : overrides.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t(
            'agent.availability_overrides.empty.translation',
            'No overrides. Add one to set different hours or close a specific date.'
          )}
        </p>
      ) : (
        <div className="space-y-4">
          {upcomingOverrides.length > 0 && (
            <ul className="space-y-2">
              {upcomingOverrides.map(o => renderOverrideItem(o))}
            </ul>
          )}
          {pastOverrides.length > 0 && (
            <Collapsible open={isPastOpen} onOpenChange={setIsPastOpen}>
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-full border border-border bg-card px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted/40"
                >
                  <span>
                    {t('agent.availability_overrides.past.translation', 'Past')}
                    <span className="ml-1.5 font-normal text-muted-foreground">
                      ({pastOverrides.length})
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      'h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200',
                      isPastOpen && 'rotate-180'
                    )}
                  />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent open={isPastOpen}>
                <ul className="mt-2 space-y-2">
                  {pastOverrides.map(o =>
                    renderOverrideItem(o, { isPast: true })
                  )}
                </ul>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}

      <Modal open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <ModalContent className="w-full max-w-lg">
          <ModalHeader>
            <ModalTitle
              description={t(
                'agent.availability_overrides.dialog_description.translation',
                'Set hours or mark this date as closed.'
              )}
            >
              {editingId != null
                ? t(
                    'agent.availability_overrides.edit_title.translation',
                    'Edit override'
                  )
                : t(
                    'agent.availability_overrides.add_title.translation',
                    'Add override'
                  )}
            </ModalTitle>
          </ModalHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="override-date">
                {t(
                  'agent.availability_overrides.date_label.translation',
                  'Date'
                )}
              </Label>
              <Popover
                open={isDatePickerOpen}
                onOpenChange={setIsDatePickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    id="override-date"
                    variant="outline"
                    className={cn(
                      'w-full justify-start text-left font-normal',
                      !formDate && 'text-muted-foreground'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {selectedFormDate ? (
                      format(selectedFormDate, 'dd/MM/yyyy')
                    ) : (
                      <span>
                        {t(
                          'agent.availability_overrides.date_placeholder.translation',
                          'DD/MM/YYYY'
                        )}
                      </span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={selectedFormDate}
                    onSelect={date => {
                      if (!date) return;
                      setFormDate(format(date, 'yyyy-MM-dd'));
                      setIsDatePickerOpen(false);
                    }}
                    locale={dateLocale.dateFns}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="override-closed"
                checked={formIsClosed}
                onCheckedChange={v => setFormIsClosed(v === true)}
              />
              <Label
                htmlFor="override-closed"
                className="text-sm font-medium cursor-pointer"
              >
                {t('agent.availability_overrides.closed.translation', 'Closed')}
              </Label>
            </div>
            {!formIsClosed && (
              <div className="flex flex-wrap items-center gap-2">
                <TimeInput24h
                  value={formStartTime}
                  onChange={setFormStartTime}
                />
                <span className="text-muted-foreground">–</span>
                <TimeInput24h
                  value={formEndTime}
                  onChange={setFormEndTime}
                  minTime={formStartTime}
                />
              </div>
            )}
          </div>
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSaving}
            >
              {t('common.cancel.translation', 'Cancel')}
            </Button>
            <Button
              type="button"
              variant="brand"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving
                ? t('common.saving.translation', 'Saving...')
                : t('common.save.translation', 'Save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {deleteId != null && (
        <Modal open={true} onOpenChange={() => setDeleteId(null)}>
          <ModalContent className="w-full max-w-lg">
            <ModalHeader>
              <ModalTitle
                description={t(
                  'agent.availability_overrides.delete_confirm_description.translation',
                  'This override will be removed. Business hours will apply for that date again.'
                )}
              >
                {t(
                  'agent.availability_overrides.delete_confirm_title.translation',
                  'Remove override?'
                )}
              </ModalTitle>
            </ModalHeader>
            <ModalFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteId(null)}
              >
                {t('common.cancel.translation', 'Cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={() => deleteId != null && handleDelete(deleteId)}
              >
                {t('common.delete.translation', 'Delete')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </div>
  );
};

export default AvailabilityOverrides;
